import api from '../core/api';

export interface CurrencyProfile {
  baseCurrency: string;
  displayCurrency: string;
  settlementCurrency: string;
  reportingCurrency: string;
  countryOverrides: { country: string; displayCurrency?: string; settlementCurrency?: string; reportingCurrency?: string }[];
  autoConvert: boolean;
  rateProvider?: string;
}

export interface ExchangeRate {
  _id: string;
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  effectiveFrom: string;
  effectiveTo?: string;
  source: string;
  isActive: boolean;
}

export interface WorkingCalendar {
  _id?: string;
  name: string;
  scope: string;
  country?: string;
  state?: string;
  city?: string;
  timezone: string;
  workingDays: number[];
  weekendDays: number[];
  holidays: { date: string; name: string; type: string; recurring?: boolean }[];
  workingHoursStart?: string;
  workingHoursEnd?: string;
  isActive: boolean;
}

export async function getLanguages() {
  const res = await api.get('/locale/public/languages');
  return res.data.data as Array<{
    code: string;
    name: string;
    nativeName: string;
    locale: string;
    rtl: boolean;
    provisioned: boolean;
    provisioning?: boolean;
    provisionError?: string | null;
    provisionedCount?: number | null;
    isDefault?: boolean;
    isActive?: boolean;
    keyCount?: number;
    coverage?: number;
    missingCount?: number;
  }>;
}

export interface TranslationCoverage {
  total: number;
  translated: number;
  reviewed: number;
  keepEnglish: number;
  stillEnglish: number;
  absent: number;
  missing: number;
  coverage: number;
}

export interface TranslationCatalogLanguage {
  code: string;
  name: string;
  nativeName: string;
  locale: string;
  rtl: boolean;
  provisioned: boolean;
  provisioning?: boolean;
  provisionError?: string | null;
  provisionedCount?: number | null;
  provisionedAt?: string | null;
  isDefault: boolean;
  isActive: boolean;
  isSource?: boolean;
  keyCount: number;
  translatedCount?: number;
  stillEnglishCount?: number;
  absentCount?: number;
  reviewedCount?: number;
  keepEnglishCount?: number;
  coverage: number;
  missingCount: number;
  complete?: boolean;
  /** Keyed by app id, including 'all'. */
  apps?: Record<string, TranslationCoverage>;
}

export async function getTranslationCatalog() {
  const res = await api.get('/locale/translation-catalog');
  return res.data.data as {
    englishKeys: number;
    sharedKeys?: number;
    unassignedNamespaces?: string[];
    sourceLanguage?: string;
    apps: Array<{
      id: string;
      label: string;
      description: string;
      namespaces: string[];
      englishCount: number;
    }>;
    namespaces: Array<{ key: string; label: string; englishCount: number }>;
    languages: TranslationCatalogLanguage[];
  };
}

export type TranslationRowStatus =
  | 'source'
  | 'translated'
  | 'reviewed'
  | 'keep_english'
  | 'still_english'
  | 'absent';

export interface TranslationRow {
  id: string;
  namespace: string;
  key: string;
  english: string;
  value: string;
  status: TranslationRowStatus;
  missing: boolean;
}

export async function getTranslationRows(language: string, app: string) {
  const res = await api.get('/locale/translation-catalog/rows', { params: { language, app } });
  return res.data.data as { language: string; app: string; namespaces: string[]; rows: TranslationRow[] };
}

export async function suggestTranslation(language: string, text: string) {
  const res = await api.post('/locale/translation-catalog/suggest', { language, text });
  return res.data.data as { language: string; text: string; value: string; sameAsEnglish: boolean };
}

export async function syncExchangeRates(baseCurrency?: string) {
  const res = await api.post('/locale/exchange-rates/sync', { baseCurrency });
  return res.data.data as { synced: number; base?: string; date?: string; provider: string; message?: string };
}

export async function provisionLanguage(code: string, force = false, wait = false) {
  const res = await api.post(`/locale/languages/${code}/provision`, { force, wait });
  return res.data.data as {
    started?: boolean;
    language?: string;
    count?: number;
    created?: number;
    updated?: number;
    skipped?: number;
    sourceKeys?: number;
  };
}

export async function getCurrencyProfile() {
  const res = await api.get('/locale/currency-profile');
  return res.data.data as CurrencyProfile;
}

export async function updateCurrencyProfile(payload: Partial<CurrencyProfile>) {
  const res = await api.put('/locale/currency-profile', payload);
  return res.data.data as CurrencyProfile;
}

export async function getExchangeRates() {
  const res = await api.get('/locale/exchange-rates');
  return res.data.data as ExchangeRate[];
}

export async function createExchangeRate(payload: { fromCurrency: string; toCurrency: string; rate: number | string; source?: string; effectiveFrom?: string }) {
  const res = await api.post('/locale/exchange-rates', payload);
  return res.data.data as ExchangeRate;
}

export async function deleteExchangeRate(id: string) {
  await api.delete(`/locale/exchange-rates/${id}`);
}

export async function convertCurrency(amount: number, from: string, to: string) {
  const res = await api.post('/locale/convert', { amount, from, to });
  return res.data.data as { amount: number; rate: number; formatted: string };
}

export async function getCalendars() {
  const res = await api.get('/locale/calendars');
  return res.data.data as WorkingCalendar[];
}

export async function saveCalendar(payload: WorkingCalendar) {
  const res = payload._id
    ? await api.put(`/locale/calendars/${payload._id}`, payload)
    : await api.post('/locale/calendars', payload);
  return res.data.data as WorkingCalendar;
}

export async function deleteCalendar(id: string) {
  await api.delete(`/locale/calendars/${id}`);
}

export async function getCountryConfig(code: string) {
  const res = await api.get(`/locale/countries/${code}`);
  return res.data.data;
}
